import type { CSSProperties } from 'react'

/** A number is taken as px; a string is any CSS length — `rem`, `dvh`, `calc()`, `clamp()` and
 *  so on. `'min-content'` as `minHeight` keeps the tile's content fully visible without a magic
 *  number: the row never gets shorter than header + body. A bare `%` resolves against the tile's grid area, so it can cap a tile but never grow
 *  its row. */
export type TileLength = number | string

export type TileSizing = {
  /** Exact height. Wins over `minHeight`/`maxHeight`. */
  height?: TileLength
  /** Floor: the tile's row grows to fit it, and the board scrolls if the rows no longer fit. */
  minHeight?: TileLength
  /** Ceiling: the tile stops growing with its row and sits at the top of its grid area. */
  maxHeight?: TileLength
}

const toCssLength = (value: TileLength | undefined): string | undefined =>
  typeof value === 'number' ? `${value}px` : value

export const getTileSizingStyle = ({
  height,
  minHeight,
  maxHeight,
}: TileSizing): CSSProperties | undefined => {
  const fixed = toCssLength(height)
  const style: CSSProperties = fixed
    ? { height: fixed, minHeight: fixed, maxHeight: fixed }
    : { minHeight: toCssLength(minHeight), maxHeight: toCssLength(maxHeight) }

  return Object.values(style).some(Boolean) ? style : undefined
}
